import { useContext, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { navigate, useNavigationPrompt, useQueryParams } from 'raviger';
import { Formik } from 'formik';
import { Form as BootstrapForm, Button, ButtonGroup, Card, Col, ListGroup, Modal, Row } from "react-bootstrap";
import * as Yup from 'yup';
import { AddressSearch, Checkbox, TextInput } from '../components/Form';
import ExistingPersonSearch from '../components/ExistingPersonSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-solid-svg-icons';
import ButtonSpinner from '../components/ButtonSpinner';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

// Form for owner and reporter Person objects.
const PersonForm = (props) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const existingOwnerRef = useRef(null);

  const id = props.id;
  const incident = "/" + props.incident
  const initial_coordinates = state?.incident?.coordinates ?? [0, 0];

  // Determine if we're in the hotline workflow.
  var is_workflow = window.location.pathname.includes("/hotline/workflow") || window.location.pathname.includes("/intake/workflow");

  // Determine if this is an owner or reporter when creating a Person.
  let is_owner = window.location.pathname.includes("/people/owner/") || window.location.pathname.endsWith("/workflow/owner") || window.location.pathname.endsWith("/workflow/return");

  // Determine if this is an intake workflow.
  let is_intake = window.location.pathname.includes("/intake/workflow/");

  // Determine if this is an intake via field team member.
  let is_field_member = window.location.pathname.endsWith("/workflow/field_member");

  // Determine if this is an return animal workflow.
  let is_return = window.location.pathname.endsWith("/workflow/return");

  // Check user navigating away when in workflow.
  const [redirectCheck, setRedirectCheck] = useState(is_workflow);
  useNavigationPrompt(redirectCheck, "Are you sure you would like to leave the animal intake workflow? No data will be saved.");

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    servicerequest_id = '',
    animal_id = '',
    owner_id = '',
  } = queryParams;

  // Regex validators.
  const phoneRegex = /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;
  const nameRegex = /^[a-z ,.'-]+$/i;
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Whether or not to skip Owner creation.
  const [skipOwner, setSkipOwner] = useState(false);
  const [isOwner, setIsOwner] = useState(props.state.stepIndex > 1 || is_owner);

  const [existingOwner, setExistingOwner] = useState(props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].id ? true : false);
  const [existingReporter, setExistingReporter] = useState(props.state.steps.reporter.id ? true : false);

  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const handleCloseStartOverModal = () => setShowStartOverModal(false);

  // Track whether or not to add another owner.
  const [addAnother, setAddAnother] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [srData, setSRData] = useState({});

  const initialData = {
    id: null,
    existing_owner: '',
    has_id: false,
    first_name: '',
    last_name: '',
    phone: '',
    alt_phone: '',
    email: '',
    comments: '',
    show_agency: is_field_member,
    agency_id: '',
    drivers_license: '',
    address: props.state && isOwner ? props.state.steps.initial.address : '',
    apartment: props.state && isOwner ? props.state.steps.initial.apartment : '',
    city: props.state && isOwner ? props.state.steps.initial.city : '',
    state: props.state && isOwner ? props.state.steps.initial.state : '',
    zip_code: props.state && isOwner ? props.state.steps.initial.zip_code : '',
    evac_zone: props.state && isOwner ? props.state.steps.initial.evac_zone : '',
    request: servicerequest_id,
    animal: is_owner ? animal_id : null,
    reporter_animal: !is_owner ? animal_id : null,
    owner: owner_id,
    latitude: props.state && isOwner ? props.state.steps.initial.latitude : null,
    longitude: props.state && isOwner ? props.state.steps.initial.longitude : null,
    change_reason: '',
    incident: state.incident.id,
    training: state.incident.training ? true : false,
    organization: state.organization.id,
  }
  let current_data = initialData;
  if (is_workflow && isOwner && props.state.steps.owners[props.state.ownerIndex]) {
    current_data = props.state.steps.owners[props.state.ownerIndex].first_name ? props.state.steps.owners[props.state.ownerIndex] : props.state.steps.initial;
  }
  else if (is_workflow) {
    current_data = props.state.steps.reporter.first_name ? props.state.steps.reporter : props.state.steps.initial;
  }
  current_data['show_agency'] = is_field_member;
  current_data['incident'] = state.incident.id;
  current_data['organization'] = state.organization.id;

  // Initial Person data.
  const [data, setData] = useState(current_data);
  const [optionFilter, setOptionFilter] = useState((isOwner && props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].first_name && !props.state.steps.owners[props.state.ownerIndex].id) || (!isOwner && props.state.steps.reporter.first_name && !props.state.steps.reporter.id) ? 'new' : 'existing');
  const [sameAddress, setSameAddress] = useState(false);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    if (id) {
      const fetchPersonData = async () => {
        // Fetch Person data.
        await axios.get('/people/api/person/' + id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            // Set phone field to be the pretty version.
            response.data['phone'] = response.data['display_phone']
            response.data['alt_phone'] = response.data['display_alt_phone']
            // Initialize change_reason on fetch to avoid warning.
            response.data['change_reason'] = '';
            response.data['has_id'] = id && isOwner ? true : false;
            setData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchPersonData();
    }
    if (servicerequest_id) {
      const fetchSRData = async () => {
        // Fetch SR data.
        await axios.get('/hotline/api/incident/' + (state ? state.incident.id : 'undefined')  + '/servicerequests/' + servicerequest_id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setSRData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchSRData();
    }
    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, isOwner]);

  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        validationSchema={Yup.object({
          has_id: Yup.boolean(),
          first_name: Yup.string()
            .max(50, 'Must be 50 characters or less')
            .matches(nameRegex, "Name is not valid")
            .required('Required'),
          last_name: Yup.string()
            .max(50, 'Must be 50 characters or less')
            .matches(nameRegex, "Name is not valid")
            .required('Required'),
          phone: Yup.string()
            .matches(phoneRegex, "Phone number is not valid")
            .min(10, "Phone number is not valid")
            .required('Required'),
          alt_phone: Yup.string()
            .matches(phoneRegex, "Phone number is not valid"),
          email: Yup.string()
            .max(200, 'Must be 200 characters or less')
            .matches(emailRegex, "Email is not valid"),
          comments: Yup.string(),
          show_agency: Yup.boolean(),
          agency_id: Yup.string().when('show_agency', {
              is: true,
              then: Yup.string()}),
          drivers_license: Yup.string(),
          address: Yup.string(),
          apartment: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          city: Yup.string(),
          state: Yup.string(),
          zip_code: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          evac_zone: Yup.string()
            .max(100, 'Must be 100 characters or less'),
          latitude: Yup.number()
            .nullable(),
          longitude: Yup.number()
            .nullable(),
          change_reason: Yup.string()
            .when('has_id', {
              is: true,
              then: Yup.string().required('Required').max(50, 'Must be 50 characters or less')}),
        })}
        onSubmit={ async (values, { setSubmitting, resetForm }) => {
          if (is_workflow) {
            values['training'] = state.incident.training ? true : false;
            if (addAnother) {
              props.onSubmit('owners', values, 'owners');
              // Reset form data with existing owner data if we have it.
              if (props.state.steps.owners[props.state.ownerIndex + 1] && Object.keys(props.state.steps.owners[props.state.ownerIndex + 1]).length) {
                setOptionFilter(props.state.steps.owners[props.state.ownerIndex + 1] && props.state.steps.owners[props.state.ownerIndex + 1].first_name && !props.state.steps.owners[props.state.ownerIndex + 1].id ? 'new' : 'existing');
                setExistingOwner(props.state.steps.owners[props.state.ownerIndex + 1] && props.state.steps.owners[props.state.ownerIndex + 1].id ? true : false);
                setData(props.state.steps.owners[props.state.ownerIndex + 1]);
                resetForm({values:props.state.steps.owners[props.state.ownerIndex + 1]});
                setEditMode(false);
                setRedirectCheck(true);
                // setIsButtonSubmitting(false);
              }
              // Otherwise reset form with blank data.
              else {
                setOptionFilter('existing');
                setExistingOwner(false);
                setData(initialData);
                resetForm({values:initialData});
                setRedirectCheck(true);
                setEditMode(false);
                // setIsButtonSubmitting(false);
              }
              existingOwnerRef.current.clear();
            }
            else if (isOwner) {
              props.onSubmit('owners', values, 'animals');
            }
            // Always continue on if reporter.
            else {
              if (skipOwner) {
                props.onSubmit('reporter', values, 'animals');
              }
              else {
                props.onSubmit('reporter', values, 'owners');
                setIsOwner(true);
                setExistingOwner(props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].id ? true : false);
                setEditMode(false);
                existingOwnerRef.current.clear();
                if (props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].first_name) {
                  setOptionFilter(props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].first_name && !props.state.steps.owners[props.state.ownerIndex].id ? 'new' : 'existing');
                  setData(props.state.steps.owners[props.state.ownerIndex]);
                  resetForm({values:props.state.steps.owners[props.state.ownerIndex]});
                }
                else {
                  setOptionFilter('existing');
                  setData(initialData);
                  resetForm({values:initialData});
                }
              }
            }
          }
          else if (data.id) {
            values['incident_slug'] = props.incident;
            values['organization_slug'] = props.organization;
            if (is_owner) {
              values['animal'] = animal_id;
            }
            else {
              values['reporter_animal'] = animal_id;
            }
            axios.put('/people/api/person/' + data.id + '/', values)
            .then(function() {
              if (servicerequest_id) {
                navigate('/' + props.organization + incident + '/hotline/servicerequest/' + servicerequest_id);
              }
              else if (animal_id) {
                navigate('/' + props.organization + incident + '/animals/' + animal_id);
              }
              else if (isOwner) {
                navigate('/' + props.organization + incident + '/people/owner/' + data.id);
              }
              else {
                navigate('/' + props.organization + incident + '/people/reporter/' + data.id);
              }
            })
            .catch(error => {
              setSubmitting(false);
              setShowSystemError(true);
            });
          }
          else {
            values['incident_slug'] = props.incident;
            values['organization_slug'] = props.organization;
            axios.post('/people/api/person/', values)
            .then(response => {
              // If SR already exists, redirect to the SR details.
              if (servicerequest_id) {
                navigate('/' + props.organization + incident + '/hotline/servicerequest/' + servicerequest_id);
              }
              // If adding from an animal, redirect to the Animal details.
              else if (animal_id) {
                navigate('/' + props.organization + incident + '/animals/' + animal_id);
              }
              // If adding from an owner, redirect to the new Owner details.
              else {
                navigate('/' + props.organization + incident + '/people/owner/' + response.data.id);
              }
            })
            .catch(error => {
              setShowSystemError(true);
              setSubmitting(false);
            });
          }
        }}
      >
        {formikProps => (
          <Card border="secondary" className={"mt-3"}>
            {(id || servicerequest_id || animal_id || owner_id) && !is_return ?
              <Card.Header as="h5" className="ps-3"><span id="back" style={{cursor: 'pointer'}} onClick={() => window.history.back()} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{id || (props.state && props.state.steps.owners.length && props.state.steps.owners[props.state.ownerIndex].id) ? "Update " : "Create "} {isOwner ? "Owner" : is_field_member ? "Field Team Member" : "Reporter"}
              {((servicerequest_id || animal_id || (owner_id && window.location.pathname.endsWith('/new'))) && existingOwner) ? <Checkbox
                id={'edit-mode'}
                name={'edit-mode'}
                front_label={"Edit Mode"}
                className={"float-end"}
                checked={editMode}
                style={{
                  transform: "scale(1.25)",
                  marginRight: "-5px",
                  marginTop: "-5px",
                  marginBottom: "-5px"
                }}
                onChange={() => setEditMode(!editMode)}
              /> : ""}
              </Card.Header>
              :
              <Card.Header as="h5" className="ps-3">
                <span id="back" style={{cursor: 'pointer'}} onClick={() => {
                  setEditMode(false);
                  setAddAnother(false);
                  existingOwnerRef.current.clear();
                  if (isOwner && props.state.ownerIndex > 0) {
                    setOptionFilter((props.state.steps.owners[props.state.ownerIndex -1] && props.state.steps.owners[props.state.ownerIndex -1].first_name && !props.state.steps.owners[props.state.ownerIndex -1].id) ? 'new' : 'existing');
                    setData(props.state.steps.owners[props.state.ownerIndex -1]);
                    formikProps.resetForm({values:props.state.steps.owners[props.state.ownerIndex -1]});
                    setExistingOwner(props.state.steps.owners.length && props.state.steps.owners[props.state.ownerIndex -1].id ? true : false);
                    props.handleBack('owners', 'owners');
                  }
                  else if (isOwner && props.state.steps.reporter.first_name) {
                    setOptionFilter((props.state.steps.reporter.first_name && !props.state.steps.reporter.id) ? 'new' : 'existing');
                    setIsOwner(false);
                    setData(props.state.steps.reporter);
                    formikProps.resetForm({values:props.state.steps.reporter});
                    props.handleBack('owners', 'reporter');
                  }
                  else {
                    setShowStartOverModal(true);
                  }
                }} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            {isOwner ? "Owner" : is_field_member ? "Field Team Member" : "Reporter"}{is_workflow ? " Information" : ""}
            {(is_workflow || is_intake) && (existingOwner || existingReporter) && formikProps.values.id ? <Checkbox
              id={'edit-mode'}
              name={'edit-mode'}
              front_label={"Edit Mode"}
              className={"float-end"}
              checked={editMode}
              style={{
                transform: "scale(1.25)",
                marginRight: "-5px",
                marginTop: "-5px",
                marginBottom: "-5px"
              }}
              onChange={() => setEditMode(!editMode)}
            /> : ""}
          </Card.Header>}
          <Card.Body className="pb-0">
          <BootstrapForm noValidate>
            {/* Only show existing owner if in a workflow/intake */}
            <span hidden={(!(is_workflow || is_intake || animal_id || (owner_id && window.location.pathname.endsWith('/new')) || servicerequest_id)) || (isOwner && props.state.steps.owners[props.state.ownerIndex] && props.state.steps.owners[props.state.ownerIndex].matching_owner) || (props.state.steps.request && props.state.steps.request.owner_objects && props.state.steps.request.owner_objects.map(owner => Number(owner.id)).includes(Number(data.id)))}>
              {!id ? <Row style={{marginTop:"-19px", marginBottom:"10px", marginLeft:"-20px", marginRight:"-20px"}}>
                <ListGroup horizontal className="border rounded w-100 pe-0" style={{height:"30px"}}>
                  <Col className="rounded">
                    <ListGroup.Item active={optionFilter === 'existing'} style={{textTransform:"capitalize", cursor:'pointer', paddingLeft:"5px", paddingRight:"5px"}} onClick={() => {existingOwnerRef.current.clear();setOptionFilter('existing');}}>
                      <div className="justify-content-center d-flex align-items-center rounded" style={{marginTop:"-2px", height:"12px"}}>
                        Use Existing {isOwner ? "Owner" : is_field_member ? "Field Team Member" : "Reporter"}
                      </div>
                    </ListGroup.Item>
                  </Col>
                  <Col className="rounded">
                    <ListGroup.Item active={optionFilter === 'new'} style={{textTransform:"capitalize", cursor:'pointer'}} onClick={() => {setExistingOwner(false); setExistingReporter(false); formikProps.resetForm({values:initialData}); setSameAddress(true); setOptionFilter('new');}}>
                      <div className="justify-content-center d-flex align-items-center rounded" style={{marginTop:"-2px", marginLeft:"4px", height:"12px"}}>
                        Create New {isOwner ? "Owner" : is_field_member ? "Field Team Member" : "Reporter"}
                      </div>
                    </ListGroup.Item>
                  </Col>
                </ListGroup>
              </Row> : ""}
              <div hidden={optionFilter === 'new'} className="mb-3">
                <label>Use Existing {isOwner ? "Owner" : is_field_member ? "Field Team Member" : "Reporter"}</label>
                <ExistingPersonSearch
                  id="existing_owner"
                  className="mt-2"
                  ref={existingOwnerRef}
                  params={{
                    field_only: is_field_member,
                    incident: props.incident,
                    organization: props.organization,
                    training: state.incident.training,
                  }}
                  onError={() => setShowSystemError(true)}
                  onSelect={(existing_owner) => {
                    if (existing_owner) {
                      existing_owner["search"] = '';
                      existing_owner["request"] = servicerequest_id;
                      setData(existing_owner);
                      setSameAddress(existing_owner.address === props.state.steps.initial.address);
                      formikProps.resetForm({values:existing_owner})
                      formikProps.setFieldValue("address", existing_owner.address);
                      formikProps.setFieldValue("search", '');
                      formikProps.setFieldValue("apartment", existing_owner.apartment);
                      formikProps.setFieldValue("city", existing_owner.city);
                      formikProps.setFieldValue("state", existing_owner.state);
                      formikProps.setFieldValue("zip_code", existing_owner.zip_code);
                      if (isOwner) {
                        setExistingOwner(true);
                      }
                      else {
                        setExistingReporter(true);
                      }
                    }
                    else {
                      setData({...initialData});
                      formikProps.resetForm({values:initialData})
                      if (isOwner) {
                        setExistingOwner(false);
                      }
                      else {
                        setExistingReporter(false);
                      }
                    }
                  }}
                  placeholder="Search by last name or phone number..."
                />
              </div>
            </span>
            <span hidden={!id && (optionFilter === 'existing') && ((isOwner && !existingOwner) || (!isOwner && !existingReporter))}>
              <Row className={"gx-2"}>
                <TextInput
                  xs="6"
                  type="text"
                  label="First Name*"
                  name="first_name"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
                <TextInput
                  xs="6"
                  type="text"
                  label="Last Name*"
                  name="last_name"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
              </Row>
              <Row className="mt-3 gx-2">
                <TextInput
                  xs={isOwner ? "2" : "3"}
                  type="text"
                  label="Phone*"
                  name="phone"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
                <TextInput
                  xs={isOwner ? "2" : "3"}
                  type="text"
                  label="Alternate Phone"
                  name="alt_phone"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
                <TextInput hidden={!isOwner}
                  xs="2"
                  type="text"
                  label="Drivers License"
                  name="drivers_license"
                  id="drivers_license"
                  disabled={(existingOwner || existingReporter) && !editMode}
                />
                <TextInput hidden={!isOwner}
                  xs="6"
                  type="text"
                  label="Evacuation Zone"
                  name="evac_zone"
                  id="evac_zone"
                  disabled={(existingOwner || existingReporter) && !editMode}
                />
              </Row>
              <Row className="mt-3 gx-2">
                <TextInput
                  hidden={!is_field_member}
                  xs="6"
                  type="text"
                  label="Agency ID"
                  name="agency_id"
                  id="agency_id"
                />
                <TextInput
                  xs="6"
                  type="text"
                  label="Email"
                  name="email"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
              </Row>
              <Row className="mt-3 mb-3">
                <TextInput
                  xs="12"
                  as="textarea"
                  label="Comments / Alternate Contact"
                  name="comments"
                  disabled={((isOwner && existingOwner) || existingReporter) && !editMode}
                />
              </Row>
              <AddressSearch formikProps={formikProps} stepIndex={props.state.ownerIndex} label={"Search for " + (isOwner ? "Owner" : "Reporter") + " Address"} incident={props.incident} show_apt={true} show_same={(!is_return) && (is_intake || (servicerequest_id && !is_workflow) || animal_id || (owner_id && window.location.pathname.endsWith('/new')) || id) ? false : true} always_show={id || owner_id || (isOwner && is_intake)} address_form={true} hidden={!isOwner} initialData={servicerequest_id ? srData : props.state.steps.initial} disabled={(existingOwner || existingReporter) && !editMode} error="Contact Address was not selected." existingOwner={isOwner ? existingOwner : existingReporter} matchingAddress={data.address === props.state.steps.initial.address && data.apartment === props.state.steps.initial.apartment} initial_coordinates={initial_coordinates} animal_id={animal_id} servicerequest_id={servicerequest_id} isOwner={isOwner} is_intake={is_intake} sameAddress={sameAddress} setSameAddress={setSameAddress} />
              <Row hidden={!id || !isOwner} className="mt-3 mb-3">
                <TextInput
                  xs="12"
                  type="text"
                  label="Change Reason"
                  name="change_reason"
                  disabled={(existingOwner || existingReporter) && !editMode}
                />
              </Row>
            </span>
          </BootstrapForm>
          </Card.Body>
            <ButtonGroup size="lg">
              {/* form save buttons */}
              {!is_workflow ?
                <ButtonSpinner isSubmitting={formikProps.isSubmitting && !skipOwner} isSubmittingText="Saving..." type="button" onClick={() => { setSkipOwner(false); formikProps.submitForm() }}>
                  {!is_intake ? <span>{!id ? "Add " + (!isOwner ? "Reporter" : "Owner") : "Save"}</span> : "Save"}
                </ButtonSpinner> : ""}
              {/* workflow buttons */}
              {is_workflow && isOwner && !is_intake ?
              <ButtonSpinner disabled={optionFilter === 'existing' && !formikProps.values.first_name} isSubmitting={formikProps.isSubmitting && addAnother} isSubmittingText="Saving..." type="button" onClick={() => {
                setAddAnother(true);
                formikProps.submitForm();
              }}>
                {props.state.steps.owners.length -1 > props.state.ownerIndex ? "Next Owner" : "Add Another"}
              </ButtonSpinner> : ""}
              {is_workflow && !isOwner ?
                <ButtonSpinner disabled={optionFilter === 'existing' && !formikProps.values.first_name} isSubmitting={formikProps.isSubmitting && !skipOwner} isSubmittingText="Saving..." type="button" onClick={() => { setSkipOwner(false); formikProps.submitForm(); }}>
                  {props.state.steps.owners.length > props.state.ownerIndex ? "Next Owner" : "Add Owner"}
                </ButtonSpinner> : ""}
              {is_workflow ?
                <ButtonSpinner disabled={optionFilter === 'existing' && !formikProps.values.first_name} isSubmitting={formikProps.isSubmitting && skipOwner} isSubmittingText="Loading..." type="button" className="btn btn-primary border" onClick={() => { setSkipOwner(true); setAddAnother(false); formikProps.submitForm(); }}>
                  Next Step
                </ButtonSpinner> : ""}
            </ButtonGroup>
          </Card>
        )}
      </Formik>
      <Modal show={showStartOverModal} onHide={handleCloseStartOverModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Start Over</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Going back to the address lookup form will reset all current data. Are you sure you want to start over?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary"
            onClick={() => {
              if (!isOwner) {
                props.handleBack('reporter', 'initial')
              }
              else {
                props.handleBack('owners', 'initial')
              }}}>Yes
          </Button>
          <Button variant="secondary" onClick={handleCloseStartOverModal}>No</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PersonForm;
